/**
 * Assisted Travel Demo - Main Component
 * 
 * Wallet-centric travel demonstrator showing the KATLAS CAR model
 * (Custody, Authority, Receipts) with bounded disclosure.
 * 
 * Sub-components are in ./TravelDemo/
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  Train,
  Send,
  Users,
  MessageSquare,
  Shield,
  Loader2,
  ArrowLeft,
  Wallet,
  Receipt,
  Package,
  ArrowRight
} from 'lucide-react';
import { ReviewerPanel } from './GovernanceViewer';

// Import sub-components
import {
  ProvenanceBadge,
  SharingBadge,
  WalletCredentialCard,
  ActorAccessCard,
  MessageBubble,
  DisruptionBanner,
  CriticalityBadge,
  BaggageStatusBadge,
  BaggageItemCard,
  CustodyChainView,
  BaggageActorCard,
  BaggageDelayBanner,
  BaggageMessageBubble,
  BaggageReceiptModal,
  ShareEvidencePackModal,
  ReceiptPackModal,
  JourneyProgress
} from './TravelDemo/index';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ==================== MAIN COMPONENT ====================

const AssistedTravelDemo = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inputMessage, setInputMessage] = useState('');
  const [showReviewer, setShowReviewer] = useState(false);
  const [showReceiptPack, setShowReceiptPack] = useState(false);
  const [showBaggageReceiptPack, setShowBaggageReceiptPack] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [activeTab, setActiveTab] = useState('wallet');
  const [baggageSubTab, setBaggageSubTab] = useState('items');
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    const loadDemo = async () => {
      try {
        const res = await fetch(`${API_URL}/api/governance/travel/demo`);
        if (res.ok) {
          const json = await res.json();
          setData(json);
        } else {
          setLoadError('Failed to load demo');
        }
      } catch (err) {
        setLoadError('Failed to load demo');
      } finally {
        setLoading(false);
      }
    };
    loadDemo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { 
    workflow, journey, wallet_credentials, actor_access_grants, 
    disruption_notice, receipt_pack, messages,
    baggage_items, baggage_custody_chain, baggage_receipts,
    baggage_actor_access, baggage_delay_notice, baggage_messages
  } = data || {};

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-cyan-400 mx-auto mb-4" />
          <div className="text-slate-400">Loading your travel wallet...</div>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center text-red-400">{loadError}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <div className="border-b border-slate-700/50 bg-slate-900/50 backdrop-blur-sm sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/demos" className="text-slate-400 hover:text-white transition-colors">
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div className="flex items-center gap-2">
                <Train className="w-5 h-5 text-cyan-400" />
                <span className="font-semibold text-white">Assisted Travel</span>
                <Badge className="bg-cyan-500/20 text-cyan-400 text-xs">Wallet View</Badge>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowReceiptPack(true)}
                className="border-slate-600 text-slate-300 hover:text-white"
                data-testid="view-receipt-pack-btn"
              >
                <Receipt className="w-4 h-4 mr-2" />
                Evidence Pack
              </Button>
              <Button
                variant={showReviewer ? "default" : "outline"}
                size="sm"
                onClick={() => setShowReviewer(!showReviewer)}
                className={showReviewer ? "bg-cyan-600" : "border-slate-600 text-slate-300"}
                data-testid="toggle-reviewer-btn"
              >
                <Shield className="w-4 h-4 mr-2" />
                {showReviewer ? 'Hide' : 'Show'} Governance
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className={`grid gap-6 ${showReviewer ? 'lg:grid-cols-2' : 'lg:grid-cols-1 max-w-3xl mx-auto'}`}>
          
          {/* Passenger View */}
          <div className="space-y-4" data-testid="passenger-view">
            {/* Journey Summary */}
            <JourneyProgress journey={journey} />

            {/* Disruption Notice */}
            <DisruptionBanner notice={disruption_notice} />

            {/* Tab Navigation */}
            <div className="flex gap-2 p-1 bg-slate-800/50 rounded-lg">
              {[
                { id: 'wallet', label: 'My Wallet', icon: Wallet },
                { id: 'access', label: 'Who Sees What', icon: Users },
                { id: 'baggage', label: 'Essential Baggage', icon: Package },
                { id: 'messages', label: 'Messages', icon: MessageSquare }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-md text-sm transition-colors ${
                    activeTab === tab.id 
                      ? 'bg-cyan-600 text-white' 
                      : 'text-slate-400 hover:text-white hover:bg-slate-700'
                  }`}
                  data-testid={`tab-${tab.id}`}
                >
                  <tab.icon className="w-4 h-4" />
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Wallet Tab */}
            {activeTab === 'wallet' && (
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader className="pb-2">
                  <CardTitle className="text-white text-base flex items-center gap-2">
                    <Wallet className="w-4 h-4 text-cyan-400" />
                    Your Travel Wallet
                    <Badge variant="outline" className="ml-auto border-slate-600 text-slate-400">
                      {wallet_credentials?.length || 0} credentials
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-sm text-slate-400 mb-4">
                    These credentials are held in your wallet. You control who sees what.
                  </div>
                  <div className="space-y-3" data-testid="wallet-credentials">
                    {wallet_credentials?.map(cred => (
                      <WalletCredentialCard key={cred.credential_id} credential={cred} />
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Access Tab */}
            {activeTab === 'access' && (
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader className="pb-2">
                  <CardTitle className="text-white text-base flex items-center gap-2">
                    <Users className="w-4 h-4 text-cyan-400" />
                    Who Sees What
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-sm text-slate-400 mb-4">
                    Each actor only sees what you've authorised for their role. No one sees more than they need.
                  </div>
                  <div className="space-y-3" data-testid="actor-access-grants">
                    {actor_access_grants?.map((grant, i) => (
                      <ActorAccessCard key={`access-${i}`} grant={grant} />
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Essential Baggage Tab */}
            {activeTab === 'baggage' && (
              <div className="space-y-4" data-testid="baggage-tab-content">
                {/* Baggage Delay Notice */}
                <BaggageDelayBanner notice={baggage_delay_notice} />
                
                {/* Baggage Sub-tabs */}
                <div className="flex gap-2 p-1 bg-slate-800/30 rounded-lg">
                  {[
                    { id: 'items', label: 'My Items', icon: Package },
                    { id: 'custody', label: 'Custody Chain', icon: ArrowRight },
                    { id: 'access', label: 'Who Sees What', icon: Users },
                    { id: 'messages', label: 'Baggage Messages', icon: MessageSquare }
                  ].map(tab => (
                    <button
                      key={tab.id}
                      onClick={() => setBaggageSubTab(tab.id)}
                      className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-md text-xs transition-colors ${
                        baggageSubTab === tab.id 
                          ? 'bg-slate-700 text-white' 
                          : 'text-slate-500 hover:text-white hover:bg-slate-700/50'
                      }`}
                      data-testid={`baggage-subtab-${tab.id}`}
                    >
                      <tab.icon className="w-3 h-3" />
                      {tab.label}
                    </button>
                  ))}
                </div>

                {/* Baggage Items Sub-tab */}
                {baggageSubTab === 'items' && (
                  <Card className="bg-slate-800/50 border-slate-700">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-white text-base flex items-center gap-2">
                        <Package className="w-4 h-4 text-cyan-400" />
                        Your Essential Baggage
                        <Badge variant="outline" className="ml-auto border-slate-600 text-slate-400">
                          {baggage_items?.length || 0} items
                        </Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-sm text-slate-400 mb-4">
                        You declared these items and authorised their handling scope. Each custodian sees only what you permitted.
                      </div>
                      <div className="space-y-3" data-testid="baggage-items-list">
                        {baggage_items?.map(item => (
                          <BaggageItemCard key={item.item_id} item={item} />
                        ))}
                      </div>
                      <div className="mt-4 pt-4 border-t border-slate-700 flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowBaggageReceiptPack(true)}
                          className="flex-1 justify-center border-slate-600 text-slate-300 hover:text-white"
                          data-testid="view-baggage-receipts-btn"
                        >
                          <Receipt className="w-4 h-4 mr-2" />
                          View Evidence
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => setShowShareModal(true)}
                          className="flex-1 justify-center bg-cyan-600 hover:bg-cyan-700"
                          data-testid="share-evidence-btn"
                        >
                          <Receipt className="w-4 h-4 mr-2" />
                          Share
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Custody Chain Sub-tab */}
                {baggageSubTab === 'custody' && (
                  <Card className="bg-slate-800/50 border-slate-700">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-white text-base flex items-center gap-2">
                        <Shield className="w-4 h-4 text-cyan-400" />
                        Custody Chain
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-sm text-slate-400 mb-4">
                        Each custody transfer is recorded with bounded disclosure. Handlers only see handling requirements—not medical details or your personal information.
                      </div>
                      <div data-testid="baggage-custody-chain">
                        <CustodyChainView custodyChain={baggage_custody_chain} />
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Baggage Access Sub-tab */}
                {baggageSubTab === 'access' && (
                  <Card className="bg-slate-800/50 border-slate-700">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-white text-base flex items-center gap-2">
                        <Users className="w-4 h-4 text-cyan-400" />
                        Who Sees What (Baggage)
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-sm text-slate-400 mb-4">
                        Each actor involved in your baggage handling sees only what you authorised for their role.
                      </div>
                      <div className="space-y-3" data-testid="baggage-actor-access">
                        {baggage_actor_access?.map((access, i) => (
                          <BaggageActorCard key={`baggage-access-${i}`} access={access} />
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Baggage Messages Sub-tab */}
                {baggageSubTab === 'messages' && (
                  <Card className="bg-slate-800/50 border-slate-700">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-white text-base flex items-center gap-2">
                        <MessageSquare className="w-4 h-4 text-cyan-400" />
                        Baggage Updates
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3 max-h-96 overflow-y-auto" data-testid="baggage-messages-list">
                        {baggage_messages?.map(msg => (
                          <BaggageMessageBubble key={msg.message_id} message={msg} />
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}

            {/* Messages Tab */}
            {activeTab === 'messages' && (
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader className="pb-2">
                  <CardTitle className="text-white text-base flex items-center gap-2">
                    <MessageSquare className="w-4 h-4 text-cyan-400" />
                    Journey Messages
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 max-h-96 overflow-y-auto mb-4" data-testid="messages-list">
                    {messages?.map(msg => (
                      <MessageBubble key={msg.message_id} message={msg} />
                    ))}
                  </div>
                  <div className="flex gap-2 pt-3 border-t border-slate-700">
                    <input
                      type="text"
                      value={inputMessage}
                      onChange={e => setInputMessage(e.target.value)}
                      placeholder="Ask about your journey..."
                      className="flex-1 bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-white text-sm focus:outline-none focus:border-cyan-500"
                      data-testid="message-input"
                    />
                    <Button className="bg-cyan-600 hover:bg-cyan-700" data-testid="send-message-btn">
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Governance Reviewer Panel */}
          {showReviewer && (
            <div className="lg:sticky lg:top-20 lg:self-start" data-testid="governance-panel">
              <ReviewerPanel 
                workflow={workflow}
                title="Journey Governance"
                subtitle="Viewing governance state for this assisted journey"
              />
            </div>
          )}
        </div>
      </div>

      {/* Receipt Pack Modal */}
      <ReceiptPackModal 
        isOpen={showReceiptPack} 
        onClose={() => setShowReceiptPack(false)} 
        receiptPack={receipt_pack}
      />

      {/* Baggage Receipt Modal */}
      <BaggageReceiptModal 
        isOpen={showBaggageReceiptPack} 
        onClose={() => setShowBaggageReceiptPack(false)} 
        receipts={baggage_receipts}
      />

      {/* Share Evidence Pack Modal */}
      <ShareEvidencePackModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
      />
    </div>
  );
};

export default AssistedTravelDemo;
